import { formatMoney } from "@vouch/shared";
import { ArrowLeft, Handshake, PiggyBank, ReceiptText, ShieldCheck } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { FinancialTimeline } from "../components/Timeline";
import { Card, Spinner } from "../components/ui";
import { usePersonSummary, useTimeline } from "../lib/hooks";

export function PersonDetailPage(): JSX.Element {
  const { id = "" } = useParams();
  const { data, isLoading } = usePersonSummary(id);
  const { data: timeline } = useTimeline("person", id);
  if (isLoading || !data) return <Spinner />;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <Link to="/connections" className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm text-slate-500 hover:text-brand-500"><ArrowLeft size={16} /> Back to people</Link>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Relationship</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">{data.person.name}</h1>
        {data.person.username && <p className="text-sm text-slate-500">@{data.person.username}</p>}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={ReceiptText} label="Shared transactions" value={data.stats.sharedTransactions} />
        <Stat icon={PiggyBank} label="Loans together" value={data.stats.sharedLoans} />
        <Stat icon={Handshake} label="Active loans" value={data.stats.activeLoans} />
        <Stat icon={ShieldCheck} label="Witness contributions" value={`${data.stats.confirmedWitnesses}/${data.stats.witnessed}`} />
      </div>

      {data.loans.length > 0 && (
        <Card>
          <h2 className="font-semibold text-slate-800">Loans with {data.person.name}</h2>
          <ul className="mt-3 divide-y divide-slate-100">
            {data.loans.map((loan) => (
              <li key={loan.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div><Link to={`/loans/${loan.id}`} className="font-medium text-slate-800 hover:text-blue-600">{loan.description || `You are the ${loan.role}`}</Link><p className="text-xs capitalize text-slate-500">{loan.status.replaceAll("_", " ")}</p></div>
                <div className="text-right"><p className="font-semibold text-slate-800">{formatMoney(loan.outstanding, loan.currency)}</p><p className="text-xs text-slate-400">of {formatMoney(loan.principal, loan.currency)}</p></div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {timeline && <FinancialTimeline data={timeline} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: typeof ReceiptText; label: string; value: number | string }): JSX.Element {
  return <Card className="p-4"><Icon size={18} className="text-blue-600" /><p className="mt-3 text-2xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></Card>;
}
